import { useEffect, useMemo, useState } from 'react'
import { buildCategoryPathFromTrail } from '../../routing'
import type { CatalogCategory } from '../../types'

type Props = {
  category: CatalogCategory
  trail: CatalogCategory[]
  categoryImage: { src: string; srcSet?: string; sizes?: string }
  toLocalizedPath: (path: string) => string
}

const HERO_IMAGE_FALLBACK_STYLE = {
  background:
    'radial-gradient(circle at 8% 14%, color-mix(in srgb, var(--theme-color-primary, #0f766e) 32%, transparent), transparent 42%), radial-gradient(circle at 88% 2%, color-mix(in srgb, var(--theme-color-accent, #0ea5e9) 24%, transparent), transparent 36%), linear-gradient(140deg, #020617 0%, color-mix(in srgb, var(--theme-color-primary, #0f766e) 26%, #0f172a) 52%, #111827 100%)',
} as const

const CategoryPageHero = ({ category, trail, categoryImage, toLocalizedPath }: Props) => {
  const [heroImageUnavailable, setHeroImageUnavailable] = useState(!categoryImage.src)
  const categoryDisplayName = (category.name || category.slug || 'Categoria').trim() || 'Categoria'

  useEffect(() => {
    setHeroImageUnavailable(!categoryImage.src)
  }, [categoryImage.src])

  const breadcrumbEntries = useMemo(
    () =>
      trail.map((entry, index) => ({
        id: entry._id,
        name: entry.name || entry.slug,
        path: buildCategoryPathFromTrail(trail.slice(0, index + 1)),
        isCurrent: index === trail.length - 1,
      })),
    [trail],
  )

  return (
    <section
      aria-labelledby="category-page-heading"
      className="relative isolate overflow-hidden bg-slate-950"
    >
      {heroImageUnavailable ? (
        <div aria-hidden="true" className="absolute inset-0" style={HERO_IMAGE_FALLBACK_STYLE} />
      ) : (
        <img
          src={categoryImage.src}
          srcSet={categoryImage.srcSet}
          sizes={categoryImage.sizes}
          alt={categoryDisplayName}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={() => setHeroImageUnavailable(true)}
          className="absolute inset-0 h-full w-full scale-[1.02] object-cover"
        />
      )}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.94)_0%,rgba(2,6,23,0.76)_38%,rgba(2,6,23,0.28)_72%,rgba(2,6,23,0.1)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(2,6,23,0.18)_52%,rgba(2,6,23,0.82)_100%)]" />

      <div className="relative mx-auto flex min-h-[250px] w-full max-w-[1740px] flex-col justify-end px-5 py-7 sm:min-h-[285px] sm:px-8 sm:py-8 lg:min-h-[330px] lg:px-12 lg:py-10">
        <div className="space-y-4 pb-1 sm:space-y-5">
          <nav
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-white/70 sm:text-sm"
            aria-label="Breadcrumb"
          >
            <a href={toLocalizedPath('/')} className="transition hover:text-white">
              Home
            </a>
            {breadcrumbEntries.map((entry) => (
              <span key={entry.id} className="inline-flex items-center gap-2">
                <span aria-hidden="true" className="text-white/40">
                  ›
                </span>
                {entry.isCurrent ? (
                  <span aria-current="page" className="font-semibold text-white">
                    {entry.name}
                  </span>
                ) : (
                  <a href={toLocalizedPath(entry.path)} className="transition hover:text-white">
                    {entry.name}
                  </a>
                )}
              </span>
            ))}
          </nav>

          <div className="max-w-3xl space-y-2.5">
            <h1 id="category-page-heading" className="text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
              {categoryDisplayName}
            </h1>
            {category.description?.trim() ? (
              <p className="max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">{category.description.trim()}</p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CategoryPageHero
